import {
  MAP_ACTION_TO_API_OPERATION_TYPE,
  MAP_API_ADD_SUBTYPES,
  MAP_API_CHANGE_SUBTYPES,
  MAP_API_CREATE_SUBTYPES,
  MAP_API_PROMPT_WITH_REFERENCES_SUBTYPES,
  STUDIO_ACTIONS,
  STUDIO_API_OPERATION_TYPES,
} from '../../activ8/studio/constants';
import { MEDIA_TYPES } from '../../constants';

const CUSTOM_RATIO_VALUE = 'CUSTOM';

const PROMPT_REQUIRED_ACTIONS = new Set([STUDIO_ACTIONS.FREE_TEXT_EDIT, STUDIO_ACTIONS.MERGE_IMAGES]);

/**
 * Maps form fields to API requestParams.
 *
 * Rule shape:
 * - param: target key in requestParams
 * - field: form field name (optional if resolve is used)
 * - skipIfBlank: omit param when trimmed value is empty
 * - dropdown: resolve value from dropdownOptions
 * - transform: (value, context) => value
 * - resolve: (context) => value — for derived values (e.g. type from action)
 *
 * Rules can be a flat array or split by mediaType when params differ.
 */
const STUDIO_OPERATION_PARAM_RULES = {
  [STUDIO_API_OPERATION_TYPES.ASPECT_RATIO]: {
    [MEDIA_TYPES.IMAGE]: [
      { param: 'prompt', field: 'message', skipIfBlank: true },
      { param: 'ratio', field: 'format', dropdown: true },
      {
        param: 'width',
        resolve: ({ fieldValues, dropdownOptions }) => {
          const ratio = getDropdownFieldValue('format', fieldValues, dropdownOptions);

          if (ratio !== CUSTOM_RATIO_VALUE) {
            return undefined;
          }

          const width = Number(fieldValues.width);
          return Number.isFinite(width) && width > 0 ? String(width) : fieldValues.width;
        },
      },
      {
        param: 'height',
        resolve: ({ fieldValues, dropdownOptions }) => {
          const ratio = getDropdownFieldValue('format', fieldValues, dropdownOptions);

          if (ratio !== CUSTOM_RATIO_VALUE) {
            return undefined;
          }

          const height = Number(fieldValues.height);
          return Number.isFinite(height) && height > 0 ? String(height) : fieldValues.height;
        },
      },
    ],
    [MEDIA_TYPES.VIDEO]: [
      { param: 'prompt', field: 'message', skipIfBlank: true },
      { param: 'ratio', field: 'format', dropdown: true },
    ],
  },
  [STUDIO_API_OPERATION_TYPES.CREATE]: {
    [MEDIA_TYPES.IMAGE]: [
      { param: 'prompt', field: 'message', skipIfBlank: true },
      { param: 'type', resolve: ({ action }) => MAP_API_CREATE_SUBTYPES[action] },
    ],
    [MEDIA_TYPES.VIDEO]: [{ param: 'prompt', field: 'message', skipIfBlank: true }],
  },
  [STUDIO_API_OPERATION_TYPES.PROMPT_WITH_REFERENCES]: {
    [MEDIA_TYPES.IMAGE]: [
      { param: 'type', resolve: ({ action }) => MAP_API_PROMPT_WITH_REFERENCES_SUBTYPES[action] },
      {
        param: 'prompt',
        field: 'message',
        skipIfBlank: ({ action }) => !PROMPT_REQUIRED_ACTIONS.has(action),
      },
    ],
    [MEDIA_TYPES.VIDEO]: [
      { param: 'prompt', field: 'message' },
      { param: 'ratio', field: 'ratio', dropdown: true },
      { param: 'audio', field: 'audio', skipIfBlank: false, transform: (value) => Boolean(value) },
    ],
  },
  [STUDIO_API_OPERATION_TYPES.CHANGE]: [
    { param: 'from', field: 'from', skipIfBlank: true },
    { param: 'to', field: 'to', skipIfBlank: true },
    { param: 'doNotChange', field: 'doNotChange', skipIfBlank: true },
    { param: 'prompt', field: 'message', skipIfBlank: true },
    { param: 'type', resolve: ({ action }) => MAP_API_CHANGE_SUBTYPES[action] },
  ],
  [STUDIO_API_OPERATION_TYPES.ADD]: [
    { param: 'prompt', field: 'message', skipIfBlank: true },
    { param: 'position', field: 'position', skipIfBlank: true },
    { param: 'size', field: 'size', skipIfBlank: true },
    { param: 'type', resolve: ({ action }) => MAP_API_ADD_SUBTYPES[action] },
  ],
  [STUDIO_API_OPERATION_TYPES.RECREATE]: [
    { param: 'url', field: 'url' },
    { param: 'prompt', field: 'message', skipIfBlank: false },
  ],
  [STUDIO_API_OPERATION_TYPES.CUTOUT]: [
    { param: 'cutout', field: 'cutout', skipIfBlank: true },
    { param: 'prompt', field: 'message', skipIfBlank: true },
  ],
  [STUDIO_API_OPERATION_TYPES.ANIMATE]: [{ param: 'prompt', field: 'message', skipIfBlank: true }],
};

const STUDIO_ATTACHMENT_SOURCES = {
  [STUDIO_ACTIONS.FREE_TEXT_EDIT]: ['attachments'],
  [STUDIO_ACTIONS.CHANGE_BRAND_LOOK]: ['attachments'],
  [STUDIO_ACTIONS.MERGE_IMAGES]: ['attachments'],
  [STUDIO_ACTIONS.ADD_LOGO]: ['file', 'attachments'],
  [STUDIO_ACTIONS.ADD_TEXT]: ['file', 'attachments'],
  [STUDIO_ACTIONS.ADD_VISUAL_ELEMENTS_FROM_TEXT]: ['file', 'attachments'],
  [STUDIO_ACTIONS.ADD_VISUAL_ELEMENTS_FROM_IMAGE]: ['file', 'attachments'],
  [STUDIO_ACTIONS.CONCEPT_RECREATE]: ['attachments'],
};

const getDropdownFieldValue = (field, fieldValues, dropdownOptions) => {
  const value = fieldValues[field];

  if (!dropdownOptions?.[field]) {
    return value;
  }

  const option = dropdownOptions[field].find((item) => item.value === value);
  return option?.value ?? value;
};

const getOperationType = (action) => {
  const operationType = MAP_ACTION_TO_API_OPERATION_TYPE[action];

  if (!operationType) {
    throw new Error(`Unsupported studio action: ${action}`);
  }

  return operationType;
};

const getParamRules = (operationType, mediaType) => {
  const rules = STUDIO_OPERATION_PARAM_RULES[operationType];

  if (!rules) {
    throw new Error(`No param rules for operation: ${operationType}`);
  }

  if (Array.isArray(rules)) {
    return rules;
  }

  return rules[mediaType] ?? [];
};

const getAttachmentSources = (action) => STUDIO_ATTACHMENT_SOURCES[action] ?? [];

const supportsAttachments = (action) => getAttachmentSources(action).length > 0;

export {
  STUDIO_ATTACHMENT_SOURCES,
  STUDIO_OPERATION_PARAM_RULES,
  getAttachmentSources,
  getOperationType,
  getParamRules,
  supportsAttachments,
};
